import React, { useState, useEffect, useRef } from 'react';
import {
    Avatar, Badge, Box, Fab, IconButton, Paper, Slide, Stack, Typography, Button, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import CallIcon from '@mui/icons-material/Call';
import VideocamIcon from '@mui/icons-material/Videocam';
import { useChat } from 'contexts/ChatContext';
import RoomList from './RoomList';
import ChatRoom from './ChatRoom';
import NewGroupDialog from './NewGroupDialog';
import GroupCall from './GroupCall';

export default function ChatOverlay() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { totalUnread, rooms, incomingCall, clearIncomingCall, messagePing } = useChat();
    const [shake, setShake] = useState(false);

    // brief shake whenever a new message ping arrives (visual companion to vibrate)
    useEffect(() => {
        if (messagePing === 0) return undefined;
        setShake(true);
        const t = setTimeout(() => setShake(false), 700);
        return () => clearTimeout(t);
    }, [messagePing]);

    const [open, setOpen] = useState(false);
    const [view, setView] = useState('list'); // 'list' | 'room'
    const [activeRoom, setActiveRoom] = useState(null);
    const [newGroupOpen, setNewGroupOpen] = useState(false);
    const [activeCall, setActiveCall] = useState(null); // { roomId, kind }

    const LAST_ROOM_KEY = 'chat:lastRoomId';
    const openRoom = (room) => {
        setActiveRoom(room);
        setView('room');
        if (room && room._id) localStorage.setItem(LAST_ROOM_KEY, room._id);
    };
    const backToList = () => { setView('list'); setActiveRoom(null); localStorage.removeItem(LAST_ROOM_KEY); };

    // Restore last-opened room once rooms are loaded (e.g. reopen → back in Global).
    const restoredRef = useRef(false);
    useEffect(() => {
        if (restoredRef.current || rooms.length === 0) return;
        const lastId = localStorage.getItem(LAST_ROOM_KEY);
        if (lastId) {
            const room = rooms.find((r) => r._id === lastId);
            if (room) { setActiveRoom(room); setView('room'); }
        }
        restoredRef.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rooms]);

    const startCall = (kind) => {
        if (!activeRoom) return;
        // initiator: notify others + open call UI (GroupCall will call_join, harmless)
        // emit handled inside GroupCall via call_join; here we also fire call_start
        setActiveCall({ roomId: activeRoom._id, kind, initiator: true });
    };

    // accept an incoming call → open call for that room
    const acceptCall = () => {
        if (!incomingCall) return;
        const room = rooms.find((r) => r._id === incomingCall.roomId) || { _id: incomingCall.roomId };
        setActiveRoom(room);
        setView('room');
        setOpen(true);
        setActiveCall({ roomId: incomingCall.roomId, kind: incomingCall.kind });
        clearIncomingCall();
    };

    const panelW = 380;
    const panelH = 600;

    return (
        <>
            {/* FAB — bottom right, does not block UI */}
            {!open && (
                <Fab
                    color={incomingCall ? 'success' : 'primary'}
                    onClick={() => setOpen(true)}
                    sx={{
                        position: 'fixed', bottom: 24, right: 24, zIndex: 1250,
                        animation: incomingCall
                            ? 'chat-ring 0.8s ease-in-out infinite'
                            : shake
                                ? 'chat-shake 0.6s ease-in-out'
                                : 'none',
                        '@keyframes chat-shake': {
                            '0%,100%': { transform: 'translateX(0)' },
                            '20%,60%': { transform: 'translateX(-6px) rotate(-8deg)' },
                            '40%,80%': { transform: 'translateX(6px) rotate(8deg)' },
                        },
                        '@keyframes chat-ring': {
                            '0%,100%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(46,125,50,0.6)' },
                            '50%': { transform: 'scale(1.12)', boxShadow: '0 0 0 14px rgba(46,125,50,0)' },
                        },
                    }}
                >
                    <Badge color="error" badgeContent={incomingCall ? '📞' : totalUnread} max={99}>
                        <ChatIcon />
                    </Badge>
                </Fab>
            )}

            {/* Panel */}
            <Slide direction={isMobile ? 'up' : 'left'} in={open} mountOnEnter unmountOnExit>
                <Paper
                    elevation={8}
                    sx={{
                        position: 'fixed',
                        zIndex: 1250,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        ...(isMobile
                            ? { inset: 0, borderRadius: 0 }
                            : { bottom: 24, right: 24, width: panelW, height: panelH, borderRadius: 3 }),
                    }}
                >
                    {/* Incoming-call banner (top) — hidden while viewing that room,
                        since ChatRoom shows its own "join ongoing call" banner there */}
                    {incomingCall && !(view === 'room' && activeRoom && activeRoom._id === incomingCall.roomId) && (
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{ p: 1, bgcolor: '#0F5E5C', color: '#fff' }}
                        >
                            {incomingCall.kind === 'video' ? <VideocamIcon /> : <CallIcon />}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="subtitle2" noWrap sx={{ color: '#fff' }}>
                                    {incomingCall.from?.fullname || 'Someone'} ກຳລັງໂທ…
                                </Typography>
                            </Box>
                            <Button size="small" variant="contained" onClick={acceptCall} sx={{ bgcolor: '#fff', color: '#0F5E5C', '&:hover': { bgcolor: '#e8f5e9' } }}>
                                ເຂົ້າຮ່ວມ
                            </Button>
                            <Button size="small" onClick={clearIncomingCall} sx={{ color: '#fff', borderColor: '#fff' }} variant="outlined">ປະຕິເສດ</Button>
                        </Stack>
                    )}

                    <Box sx={{ flex: 1, minHeight: 0 }}>
                        {view === 'list' ? (
                            <RoomList onOpenRoom={openRoom} onNewGroup={() => setNewGroupOpen(true)} onClose={() => setOpen(false)} />
                        ) : (
                            <ChatRoom
                                room={activeRoom}
                                onBack={backToList}
                                onStartCall={startCall}
                                onJoinCall={(kind) => setActiveCall({ roomId: activeRoom._id, kind, initiator: false })}
                                inCall={!!activeCall && activeCall.roomId === activeRoom._id}
                                onClose={() => setOpen(false)}
                            />
                        )}
                    </Box>
                </Paper>
            </Slide>

            <NewGroupDialog open={newGroupOpen} onClose={() => setNewGroupOpen(false)} onCreated={(room) => openRoom(room)} />

            {activeCall && (
                <CallLauncher
                    call={activeCall}
                    onEnd={() => setActiveCall(null)}
                />
            )}
        </>
    );
}

// Small wrapper: fires call_start for the initiator, then renders GroupCall.
function CallLauncher({ call, onEnd }) {
    const { emit } = useChat();
    useEffect(() => {
        if (call.initiator) emit('chat:call_start', { roomId: call.roomId, kind: call.kind });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return <GroupCall roomId={call.roomId} kind={call.kind} onEnd={onEnd} />;
}
